// A Run panel from what the Application API answers (docs/cockpit/spec.md): the Run's Audit Log entries grouped
// into its steps, each entry with who acted and when, every artefact that went out or came in (messages exactly
// as sent or received, Documents, photos, challans, receipts), why it happened (the rule, Playbook line or
// approval the entry names), and the Credits each step used. Everyone involved is listed once, for Call.
// Its words are in the person's language, English unless a catalogue is given. Every entry is worded by the names the
// Application API sends with it (#462): who acted by their name, or LAMP and the kind of work it did; the Run by what
// it is about; a step by its name. No id and no event's code name is ever shown.
import type { AuditEntry, RunCredits } from "../api.js";
import { CATALOGUES, type Words } from "../i18n/words.js";
import { stopReasonIn } from "./stop-reasons.js";

export const ARTEFACT_KINDS = ["message", "document", "photo", "challan", "receipt", "file"] as const;
export type ArtefactKind = (typeof ARTEFACT_KINDS)[number];

/** Something that went out or came in on the Run, openable in one tap. */
export interface Artefact {
  readonly key: string;
  readonly kind: ArtefactKind;
  readonly title: string;
  /** Where it opens: an Application API path. Messages open in the panel instead. */
  readonly href?: string;
  /** A message: its words exactly as sent or received, which way it went, on which Channel, with whom. */
  readonly message?: {
    readonly direction: "sent" | "received";
    readonly text: string;
    readonly channel: string;
    readonly party: string;
  };
}

/** One Audit Log entry on the Run, as the panel shows it. */
export interface PanelEntry {
  readonly sequence: number;
  readonly action: string;
  readonly what: string;
  readonly who: string;
  readonly at: string;
  readonly artefacts: readonly Artefact[];
  /** Why it happened, one line each; empty when the Audit Log records no reason beyond who acted. */
  readonly why: readonly string[];
}

export interface PanelStep {
  /** The Business Tree node of the step, or null for entries on no node. */
  readonly node: string | null;
  /** The step's name in the Business Tree, when the Application API named it. */
  readonly name?: string;
  readonly entries: readonly PanelEntry[];
  readonly milliCredits: number;
}

/** Someone involved in the Run; the phone, when LAMP has one, is for Call. */
export interface Involved {
  readonly name: string;
  readonly phone?: string;
}

export interface RunPanel {
  readonly run: string;
  /** What the Run is about, in words ("Sales order SO/26-27/0010"); absent when no entry names it. */
  readonly about?: string;
  /** The Business Object the Run is about (`sales_order`, `enquiry`, ...), when an entry names it. */
  readonly aboutObject?: string;
  readonly steps: readonly PanelStep[];
  readonly involved: readonly Involved[];
  /** Every Credit the Run used, including any not tied to a step shown. */
  readonly milliCredits: number;
  readonly unplacedMilliCredits: number;
}

export const MESSAGE_SENT = "MessageSent";
export const MESSAGE_RECEIVED = "MessageReceived";
export const STEP_ACTION = "RunAdvanced";
/** A Received File's route started work on the Run. */
export const RECEIVED_FILE_ROUTED = "ReceivedFileRouted";
/** A file LAMP does not read, kept against the Run or the order it is on, with the person's words (#489). */
export const RECEIVED_FILE_KEPT = "ReceivedFileKept";
/** Who the Audit Log's System Actor is, in the panel. */
export const LAMP = "LAMP";

const text = (value: unknown): string | undefined => (typeof value === "string" && value !== "" ? value : undefined);
const record = (value: unknown): Readonly<Record<string, unknown>> | undefined =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : undefined;
const list = (value: unknown): readonly unknown[] => (Array.isArray(value) ? (value as unknown[]) : []);
const kindOf = (value: unknown): ArtefactKind => ARTEFACT_KINDS.find((k) => k === value && k !== "message") ?? "file";

/** Only same-origin Application API paths open from the panel: never a link a message carried in. */
const apiPath = (value: unknown): string | undefined => {
  const path = text(value);
  return path?.startsWith("/") && !path.startsWith("//") ? path : undefined;
};

export const documentHref = (id: string) => `/api/documents/${encodeURIComponent(id)}/pdf`;

const objectNames = (words: Words): Readonly<Record<string, string>> => ({
  document: words.runPanelObjectDocument,
  invoice: words.runPanelObjectInvoice,
  delivery_challan: words.runPanelObjectDeliveryChallan,
  challan: words.runPanelObjectChallan,
  receipt: words.runPanelObjectReceipt,
  quotation: words.runPanelObjectQuotation,
  sales_order: words.runPanelObjectSalesOrder,
  purchase_order: words.runPanelObjectPurchaseOrder,
  enquiry: words.runPanelObjectEnquiry,
  work_order: words.runPanelObjectWorkOrder,
});
export const objectName = (object: string, words: Words = CATALOGUES.en) =>
  objectNames(words)[object] ?? object.replaceAll("_", " ");

/** A task class in plain words: `extract_order_fields` is "extract order fields". */
export const workOf = (taskClass: string) => taskClass.replaceAll(/[_.-]+/g, " ").trim();

/** A person the entry names, by their name; never their id. */
export const personNamed = (entry: AuditEntry, id: string, words: Words = CATALOGUES.en) =>
  entry.names?.people[id] ?? words.auditAPerson;

/** Who did it: a person by their name, or LAMP and the kind of work it did. */
export function whoOf(entry: AuditEntry, words: Words = CATALOGUES.en): string {
  if (entry.actor.kind === "person") {
    const name = personNamed(entry, entry.actor.id, words);
    return entry.agent ? words.runPanelWhoAgent(name, workOf(entry.agent)) : name;
  }
  return entry.agent ? words.auditLampDoing(workOf(entry.agent)) : LAMP;
}

/** What the entry's Run is about, in words: "Sales order SO/26-27/0010", "the Run with Patil Pumps". */
export function aboutOf(entry: AuditEntry, words: Words = CATALOGUES.en): string | undefined {
  const about = entry.names?.about;
  if (!about) return undefined;
  if (about.object === "counterparty") return words.auditRunWith(about.label);
  if (about.object === "value_stream") return about.label;
  return `${objectName(about.object, words)} ${about.label}`;
}

/** The step the entry is on, by name; its node's code when the Application API did not name it. */
const stepOf = (entry: AuditEntry) => entry.names?.step ?? entry.node ?? "";

/** The Task a Work Item entry is about, by its name. */
const taskOf = (entry: AuditEntry, words: Words) => text(entry.detail["name"]) ?? entry.names?.step ?? words.auditATask;

/** An action's code name as plain words, for an action no catalogue words yet: `RingChanged` is "Ring changed". */
export const plainAction = (action: string) => {
  const words = action.replaceAll(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};

function partyOf(detail: Readonly<Record<string, unknown>>): Involved | undefined {
  const party = record(detail["party"]);
  const name = text(party?.["name"]);
  if (name === undefined) return undefined;
  const phone = text(party?.["phone"]);
  return phone ? { name, phone } : { name };
}

function artefactsOf(entry: AuditEntry, words: Words): Artefact[] {
  const { detail } = entry;
  if (entry.action === MESSAGE_SENT || entry.action === MESSAGE_RECEIVED) {
    const direction = entry.action === MESSAGE_SENT ? "sent" : "received";
    const party = partyOf(detail)?.name ?? words.runPanelSomeone;
    const channel = text(detail["channel"]) ?? words.runPanelAChannel;
    const message: Artefact = {
      key: `${entry.sequence}:message`,
      kind: "message",
      title: direction === "sent" ? words.runPanelMessageTo(party) : words.runPanelMessageFrom(party),
      message: { direction, text: typeof detail["text"] === "string" ? detail["text"] : "", channel, party },
    };
    const attachments = list(detail["attachments"]).flatMap((a, i): Artefact[] => {
      const attachment = record(a);
      const href = apiPath(attachment?.["href"]);
      if (!attachment || href === undefined) return [];
      const kind = kindOf(attachment["kind"]);
      return [{ key: `${entry.sequence}:${i}`, kind, title: text(attachment["name"]) ?? kind, href }];
    });
    return [message, ...attachments];
  }
  if (entry.action === RECEIVED_FILE_ROUTED || entry.action === RECEIVED_FILE_KEPT) {
    // A file a person handed LAMP that started this Run, or was kept on it: it opens from the Application API, never a
    // link it carried.
    const file = record(detail["file"]);
    const href = apiPath(file?.["href"]);
    if (!file || href === undefined) return [];
    return [{ key: `${entry.sequence}:file`, kind: "file", title: text(file["name"]) ?? words.runPanelFile, href }];
  }
  if (entry.action === "DocumentIssued") {
    const id = text(detail["id"]) ?? entry.object?.id ?? undefined;
    const object = text(detail["object"]) ?? entry.object?.name ?? "document";
    const number = text(detail["number"]);
    const title = number ? `${objectName(object, words)} ${number}` : objectName(object, words);
    return [{ key: `${entry.sequence}:document`, kind: "document", title, ...(id ? { href: documentHref(id) } : {}) }];
  }
  return [];
}

/** What the entry records, as a sentence in the Owner's words. */
export function whatOf(entry: AuditEntry, words: Words = CATALOGUES.en): string {
  const { detail } = entry;
  const run = aboutOf(entry, words) ?? words.auditTheRun;
  switch (entry.action) {
    case STEP_ACTION:
      return words.runPanelEnteredStep(run, stepOf(entry));
    case "RunCompleted":
      return words.runPanelRunCompleted(run);
    case "RunBlocked":
      return words.runPanelRunBlocked(run);
    case "RunPaused":
      return words.runPanelRunPaused(run);
    case "RunResumed":
      return words.runPanelRunResumed(run);
    case "RunMigrated":
      return words.runPanelRunMigrated(run);
    case "RunHalted":
      return words.auditRunHalted(run);
    case "IncidentRaised":
      return words.auditIncidentRaised(run);
    case "IncidentResolved":
      return words.auditIncidentResolved(run);
    case "WorkItemNudged":
      return words.runPanelNudged(text(detail["role"]), run);
    case "WorkItemEscalated":
      return words.runPanelEscalated(text(detail["escalatedTo"]), run);
    case "DeviationRaised":
      return words.runPanelDeviationRaised(run, text(detail["what"]) ?? text(detail["kind"]));
    case "DeviationApproved":
      return words.runPanelDeviationApproved(run, text(detail["kind"]));
    case "DeviationDeclined":
      return words.auditDeviationDeclined(run, text(detail["reason"]));
    case "SuggestionRaised":
      return words.auditSuggestionRaised(run);
    case "DocumentIssued": {
      const object = text(detail["object"]) ?? entry.object?.name ?? "document";
      const number = text(detail["number"]);
      // A Run about the Document itself is not named twice.
      const about = number !== undefined && entry.names?.about?.label === number ? undefined : aboutOf(entry, words);
      return words.runPanelIssued(objectName(object, words), number, about);
    }
    case "RunStarted":
      return words.auditRunStarted(run);
    case "WorkItemOpened":
      return words.auditTaskOpened(taskOf(entry, words), run);
    case "WorkItemConfirmed":
      return words.auditTaskConfirmed(taskOf(entry, words), run, detail["edited"] === true);
    case "WorkItemSentBack":
      return words.auditTaskSentBack(taskOf(entry, words), run, text(detail["reason"]));
    case "WorkItemDone":
      return words.auditTaskDone(taskOf(entry, words), run);
    case "HoldReleased":
      return words.auditHoldReleased(run, text(detail["reason"]));
    case "PostingMade": {
      const object = text(detail["object"]) ?? entry.object?.name ?? "document";
      return words.auditPosted(objectName(object, words), text(detail["number"]), aboutOf(entry, words));
    }
    case "AgentStepTaken":
      return words.auditAgentStepTaken(run);
    case "JudgeSampled":
      return words.auditJudgeSampled(run);
    case RECEIVED_FILE_ROUTED:
      return words.runPanelFileStarted(text(record(detail["file"])?.["name"]) ?? words.runPanelFile);
    case RECEIVED_FILE_KEPT:
      return words.runPanelFileKept(
        text(record(detail["file"])?.["name"]) ?? words.runPanelFile,
        text(detail["words"]),
      );
    case "AgentResultChecked":
      return detail["passed"] === true ? words.runPanelChecksPassed : words.runPanelChecksFailed;
    case "AgentResultReviewed":
      if (detail["outcome"] === "confirmed") return words.runPanelConfirmed;
      return detail["outcome"] === "edited" ? words.runPanelCorrected : words.runPanelSentBack;
    case "AgentResultCompensated":
      return words.runPanelUndid(text(detail["reason"]));
    case MESSAGE_SENT:
    case MESSAGE_RECEIVED: {
      const party = partyOf(detail)?.name ?? words.runPanelSomeone;
      const channel = text(detail["channel"]);
      return entry.action === MESSAGE_SENT
        ? words.runPanelSentMessage(party, channel)
        : words.runPanelReceivedMessage(party, channel);
    }
    default:
      return words.auditActions[entry.action] ?? plainAction(entry.action);
  }
}

/** A Task's code leading a reason, e.g. "6.2.1.1: ". */
const TASK_CODE = /^\d+(?:\.\d+)+:\s*/;

/** Why the entry happened: the rules, Playbook lines and approvals its event names. */
function whyOf(entry: AuditEntry, words: Words): string[] {
  const { detail } = entry;
  const why: string[] = [];
  const rule = text(detail["rule"]);
  if (rule) why.push(words.runPanelRule(rule));
  const playbookLine = text(detail["playbookLine"]);
  if (playbookLine) why.push(words.runPanelPlaybookLine(playbookLine));
  switch (entry.action) {
    case STEP_ACTION: {
      why.push(words.runPanelGoesOn(text(detail["from"])));
      break;
    }
    case "RunBlocked": {
      // The Run interpreter leads the reason with the Task's code, which is LAMP's vocabulary, not the person's.
      // Why it stops, recorded as a stop code, is worded in the person's language (#570).
      const reason = text(detail["reason"])?.replace(TASK_CODE, "");
      if (reason) why.push(words.runPanelBlockedBecause(stopReasonIn(words, reason)));
      break;
    }
    case "WorkItemEscalated": {
      if (detail["because"] === "overdue") why.push(words.runPanelOverdue(text(detail["expectedTime"])));
      for (const problem of list(detail["problems"]))
        if (typeof problem === "string") why.push(stopReasonIn(words, problem));
      break;
    }
    case "WorkItemNudged": {
      const expected = text(detail["expectedTime"]);
      if (expected) why.push(words.runPanelExpected(expected));
      break;
    }
    case "DeviationRaised": {
      for (const field of ["what", "why", "impact"] as const) {
        const said = text(detail[field]);
        if (said) why.push(said);
      }
      const approver = text(detail["approver"]);
      if (approver) why.push(words.runPanelApprovalRule(approver));
      break;
    }
    case "DeviationApproved": {
      const by = text(detail["approvedBy"]);
      if (by) why.push(words.runPanelApprovedBy(personNamed(entry, by, words)));
      break;
    }
    default:
      break;
  }
  const approvedBy = text(detail["approvedBy"]);
  if (approvedBy && entry.action !== "DeviationApproved")
    why.push(words.runPanelApprovedBy(personNamed(entry, approvedBy, words)));
  if (entry.actor.kind === "person" && entry.action !== "DeviationApproved")
    why.push(words.runPanelConfirmedBy(personNamed(entry, entry.actor.id, words)));
  return why;
}

/** The step node `node` belongs to: the longest step node it is, or sits under. */
function stepFor(node: string | null, steps: readonly string[]): string | null {
  if (node === null) return null;
  const under = steps.filter((s) => node === s || node.startsWith(`${s}.`));
  return under.reduce<string | null>((best, s) => (best === null || s.length > best.length ? s : best), null) ?? node;
}

export function runPanel(
  run: string,
  entries: readonly AuditEntry[],
  credits: RunCredits,
  words: Words = CATALOGUES.en,
): RunPanel {
  const ordered = [...entries].sort((a, b) => a.sequence - b.sequence);
  const stepNodes = [
    ...new Set(ordered.filter((e) => e.action === STEP_ACTION && e.node !== null).map((e) => e.node)),
  ].filter((n): n is string => n !== null);

  const groups = new Map<string | null, PanelEntry[]>();
  const stepNames = new Map<string, string>();
  for (const entry of ordered) {
    if (entry.node !== null && entry.names?.step) stepNames.set(entry.node, entry.names.step);
  }
  for (const entry of ordered) {
    const step = stepFor(entry.node, stepNodes);
    const group = groups.get(step) ?? [];
    group.push({
      sequence: entry.sequence,
      action: entry.action,
      what: whatOf(entry, words),
      who: whoOf(entry, words),
      at: entry.occurredAt,
      artefacts: artefactsOf(entry, words),
      why: whyOf(entry, words),
    });
    groups.set(step, group);
  }

  const creditsByStep = new Map<string | null, number>();
  let unplaced = 0;
  for (const [node, milli] of Object.entries(credits.byNode)) {
    const step = stepFor(node, stepNodes);
    if (groups.has(step)) creditsByStep.set(step, (creditsByStep.get(step) ?? 0) + milli);
    else unplaced += milli;
  }

  const involved = new Map<string, Involved>();
  for (const entry of ordered) {
    const party = partyOf(entry.detail);
    if (party && !involved.get(party.name)?.phone) involved.set(party.name, party);
    // People LAMP has a name for: an unnamed one is no one to call.
    const name = entry.actor.kind === "person" ? entry.names?.people[entry.actor.id] : undefined;
    if (name !== undefined && !involved.has(name)) involved.set(name, { name });
  }
  const named = ordered.find((e) => e.names?.about !== undefined);
  const about = named ? aboutOf(named, words) : undefined;

  return {
    run,
    ...(about !== undefined ? { about } : {}),
    ...(named?.names?.about ? { aboutObject: named.names.about.object } : {}),
    steps: [...groups].map(([node, stepEntries]) => {
      const name = node === null ? undefined : stepNames.get(node);
      return {
        node,
        ...(name !== undefined ? { name } : {}),
        entries: stepEntries,
        milliCredits: creditsByStep.get(node) ?? 0,
      };
    }),
    involved: [...involved.values()],
    milliCredits: credits.milliCredits,
    unplacedMilliCredits: unplaced,
  };
}

/** A phone number as a `tel:` link. */
export const telHref = (phone: string) => `tel:${phone.replace(/[^+\d]/g, "")}`;
